/**
 * BatchManifestDrawer (S6.2) — 裝櫃明細 read view: the report SAMPLE panel
 * as a screen.
 *
 * Per-factory roll-up (代碼 + 工廠名稱 + 箱號範圍 + 箱數 + 材積 + 備註) with a
 * TOTAL summary row, the batch capture facts (mode/櫃型/最後交期日/批號/
 * Sample 資訊), the CBM utilization bar, and the 改數量 correction log.
 * Numbers come from `buildManifest` — the same pure roll-up vitest asserts.
 *
 * DOCS-HANDOFF RULE (plan §S6.2 / ux-lens #9): the 報關行 / customer-doc
 * download stays HIDDEN until its dataset is spec'd (Joe/林芊) — same rule as
 * the bank-export stub. No mock-download button ships on this surface.
 */

import { useMemo } from 'react';
import { Descriptions, Drawer, Progress, Table, Tag, Typography, theme } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { renderDash } from '@/components/common/TableContainer';
import { radiusTokens } from '@/styles/tokens';
import type { ShipmentBatch, ShipmentBatchStatus } from '@/types/api/shipping';
import {
  CONTAINER_CAPACITY_CBM,
  buildManifest,
  toCaptured,
  utilizationPct,
} from '../batchCapture';
import type { ManifestFactoryRow } from '../batchCapture';

const { Text } = Typography;

/** Matches the list page / OCC BatchLens status mapping. */
const STATUS_TAG_COLOR: Record<ShipmentBatchStatus, string> = {
  open: 'default',
  cut_off: 'orange',
  departed: 'cyan',
  closed: 'green',
};

export interface BatchManifestDrawerProps {
  open: boolean;
  /** The batch whose manifest is shown (null while closed). */
  batch: ShipmentBatch | null;
  onClose: () => void;
}

const formatCartonRange = (row: ManifestFactoryRow): string | null => {
  if (row.cartonFrom == null && row.cartonTo == null) return null;
  if (row.cartonFrom != null && row.cartonTo != null) {
    return row.cartonFrom === row.cartonTo
      ? `${row.cartonFrom}`
      : `${row.cartonFrom}–${row.cartonTo}`;
  }
  return `${row.cartonFrom ?? row.cartonTo}`;
};

const BatchManifestDrawer = ({ open, batch, onClose }: BatchManifestDrawerProps) => {
  const { t } = useTranslation(['shipping', 'common']);
  const { token } = theme.useToken();

  const captured = useMemo(() => (batch ? toCaptured(batch) : null), [batch]);
  const manifest = useMemo(
    () => (captured ? buildManifest(captured.lines) : null),
    [captured]
  );

  const containerType = captured?.containerType ?? null;
  const capacity = containerType ? CONTAINER_CAPACITY_CBM[containerType] : null;
  const pct = manifest ? utilizationPct(manifest.totals.cbm, containerType) : null;
  const sealed = captured != null && captured.status !== 'open';
  const shortLoadedLines = sealed
    ? (captured?.lines ?? []).filter((line) => line.qtyLoaded < line.qtyPlanned).length
    : 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={760}
      destroyOnHidden
      title={
        captured ? (
          <div className="tw-flex tw-items-center tw-gap-2">
            <span>{t('shipping:batch.manifest.title', { batchNumber: captured.batchNumber })}</span>
            <Tag color={STATUS_TAG_COLOR[captured.status]} style={{ margin: 0 }}>
              {t(`shipping:batch.status.${captured.status}`)}
            </Tag>
          </div>
        ) : (
          t('shipping:batch.manifest.titleEmpty')
        )
      }
    >
      {captured && manifest && (
        <>
          {/* Batch capture facts — Board 5 fields */}
          <Descriptions column={2} size="small" style={{ marginBottom: token.marginLG }}>
            <Descriptions.Item label={t('shipping:batch.fields.mode')}>
              {t(`shipping:batch.mode.${captured.mode}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('shipping:batch.fields.containerType')}>
              {containerType ? t(`shipping:batch.containerType.${containerType}`) : renderDash()}
            </Descriptions.Item>
            <Descriptions.Item label={t('shipping:batch.cutOffModal.containerLabel')}>
              {renderDash(captured.containerNumber)}
            </Descriptions.Item>
            <Descriptions.Item label={t('shipping:batch.cutOffModal.sealLabel')}>
              {renderDash(captured.sealNumber)}
            </Descriptions.Item>
            <Descriptions.Item label={t('shipping:batch.fields.etd')}>
              {renderDash(captured.etd)}
            </Descriptions.Item>
            <Descriptions.Item label={t('shipping:batch.fields.eta')}>
              {renderDash(captured.eta)}
            </Descriptions.Item>
            <Descriptions.Item label={t('shipping:batch.fields.finalDeliveryDate')}>
              {renderDash(captured.finalDeliveryDate)}
            </Descriptions.Item>
            <Descriptions.Item label={t('shipping:batch.fields.lotNumber')}>
              {renderDash(captured.lotNumber)}
            </Descriptions.Item>
            <Descriptions.Item label={t('shipping:batch.fields.sampleInfo')} span={2}>
              {renderDash(captured.sampleInfo)}
            </Descriptions.Item>
          </Descriptions>

          {/* CBM utilization — same numbers as the cut-off capture grid */}
          <div style={{ marginBottom: token.marginLG }}>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('shipping:batch.utilization.label')}
            </Text>
            {capacity != null && pct != null ? (
              <>
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Progress
                    percent={Math.min(100, pct)}
                    showInfo={false}
                    size="small"
                    status={pct > 100 ? 'exception' : 'normal'}
                    style={{ flex: 1, margin: 0 }}
                  />
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
                </div>
                <Text style={{ fontSize: token.fontSizeSM, fontVariantNumeric: 'tabular-nums' }}>
                  {t('shipping:batch.utilization.value', {
                    cbm: manifest.totals.cbm,
                    capacity,
                    containerType: t(`shipping:batch.containerType.${containerType as string}`),
                  })}
                </Text>
              </>
            ) : (
              <Text
                style={{
                  display: 'block',
                  fontSize: token.fontSizeSM,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {t('shipping:batch.utilization.totalOnly', { cbm: manifest.totals.cbm })}
              </Text>
            )}
          </div>

          {shortLoadedLines > 0 && (
            <div
              className="tw-flex tw-items-center tw-gap-2"
              style={{ marginBottom: token.marginLG, color: token.colorWarning }}
            >
              <WarningOutlined aria-label={t('shipping:batch.shortLoadTag')} />
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {t('shipping:batch.manifest.shortLoadedLines', { count: shortLoadedLines })}
              </span>
            </div>
          )}

          {/* Per-factory manifest — 代碼+箱號範圍+箱數+備註+材積 + TOTAL */}
          <Text strong style={{ display: 'block', marginBottom: token.marginXS }}>
            {t('shipping:batch.manifest.sectionTitle')}
          </Text>
          <Table<ManifestFactoryRow>
            size="small"
            rowKey="key"
            dataSource={manifest.rows}
            pagination={false}
            locale={{ emptyText: t('shipping:batch.manifest.noLines') }}
            columns={[
              {
                title: t('shipping:batch.manifest.factoryCode'),
                dataIndex: 'factoryCode',
                key: 'factoryCode',
                width: 90,
                render: (value: string | null) => renderDash(value),
              },
              {
                title: t('shipping:batch.manifest.factoryName'),
                dataIndex: 'factoryName',
                key: 'factoryName',
                render: (value: string) => (
                  <span style={{ fontWeight: token.fontWeightStrong }}>{value}</span>
                ),
              },
              {
                title: t('shipping:batch.manifest.cartonRange'),
                key: 'cartonRange',
                width: 110,
                align: 'right',
                render: (_: unknown, row) => (
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatCartonRange(row) ?? '-'}
                  </span>
                ),
              },
              {
                title: t('shipping:batch.manifest.cartonCount'),
                dataIndex: 'cartonCount',
                key: 'cartonCount',
                width: 80,
                align: 'right',
                render: (value: number | null) => (
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value ?? '-'}</span>
                ),
              },
              {
                title: t('shipping:batch.manifest.cbm'),
                dataIndex: 'cbm',
                key: 'cbm',
                width: 90,
                align: 'right',
                render: (value: number) => (
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value.toFixed(2)}</span>
                ),
              },
              {
                title: t('shipping:batch.manifest.note'),
                dataIndex: 'notes',
                key: 'notes',
                width: 170,
                render: (notes: string[]) =>
                  notes.length ? (
                    <span style={{ fontSize: token.fontSizeSM }}>{notes.join('；')}</span>
                  ) : (
                    renderDash()
                  ),
              },
            ]}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <span style={{ fontWeight: token.fontWeightStrong }}>
                    {t('shipping:batch.manifest.total')}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  {renderDash()}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <span
                    style={{
                      fontWeight: token.fontWeightStrong,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {manifest.totals.cartonCount}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <span
                    style={{
                      fontWeight: token.fontWeightStrong,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {manifest.totals.cbm.toFixed(2)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5}>{renderDash()}</Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />

          {/* 改數量 correction log — the low-privilege loop's audit trail */}
          {captured.corrections.length > 0 && (
            <div style={{ marginTop: token.marginLG }}>
              <Text strong style={{ display: 'block', marginBottom: token.marginXS }}>
                {t('shipping:batch.manifest.correctionsTitle')}
              </Text>
              {captured.corrections.map((correction) => (
                <div
                  key={correction.id}
                  style={{
                    padding: '12px 16px',
                    marginBottom: token.marginXS,
                    borderRadius: radiusTokens.card,
                    backgroundColor: token.colorFillQuaternary,
                  }}
                >
                  <div className="tw-flex tw-items-center tw-justify-between">
                    <Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                      {correction.correctedAt.slice(0, 10)}
                    </Text>
                    <Text
                      style={{
                        fontSize: token.fontSizeSM,
                        fontVariantNumeric: 'tabular-nums',
                        color: token.colorTextSecondary,
                      }}
                    >
                      {correction.changes
                        .map((change) => `${change.sku} ${change.qtyFrom} → ${change.qtyTo}`)
                        .join('；')}
                    </Text>
                  </div>
                  <Text style={{ display: 'block' }}>{correction.reason}</Text>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

export default BatchManifestDrawer;
